import Constants from '@/Constants';
import { EntityType } from '@/models/EntityTypes';
import { ProgramClass } from '@/models/ProgramClass';
import { WorkshopMeeting } from '@/models/WorkshopMeeting';
import { currentDate, getFormattedExpirationDate, getValidUrl, isExpireDateViewByStatus } from '@/services/UtilService';
import { getWorkshopByCourseId } from '@/services/WorkshopService';
import { LocaleData } from '@/translations/LocaleData';
import { isAfter, isBefore } from 'date-fns';
import { default as React, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'underscore';
import { CardMode } from '../../models/CardMode';
import { CourseEnrollment } from '../../models/CourseEnrollment';
import List from '../../models/List';
import { courseService } from '../../services/CourseService';
import { ApplicationState } from '../../store';
import CourseSelection from '../CourseSelection';
import FavoriteSection from '../FavoriteSection';
import './style.scss';

interface Props {
    course: List;
    mode: CardMode;
    customClass?: string;
    hasCurrentContent?: boolean;
    courseEnrollment?: CourseEnrollment;
    hideStatus?: boolean;
    isCourseProgram?: boolean;
    programClass?: ProgramClass;
    disabled?: boolean;
}

const DAYS_TO_WORKSHOP_MEETING_TO_ALERT: number = 15;

const LMSCourseCard: React.FC<Props> = (props: Props) => {
    const { course, mode, courseEnrollment, hasCurrentContent, hideStatus, programClass, disabled, isCourseProgram = true } = props;

    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);
    const personId: number | undefined = useSelector((state: ApplicationState) => state.identity.data.person.id);

    const [percentCompletedCourses, setPercentCompletedCourses] = useState<string>('0');

    const isExpired = courseService.isExpired(courseEnrollment, programClass);
    const canFavorite: boolean = mode === 'view' && isCourseProgram && course.ownerId !== personId && !disabled;
    const courseLessonsAmount: number = course.lessonAmount || course?.lessons?.length || 0;
    const workshop = getWorkshopByCourseId(course.id, programClass?.workshops);
    const workshopNextMeeting: WorkshopMeeting | undefined = workshop?.nextMeeting;

    const alertTitleNameClass: string =
        !!workshopNextMeeting && workshopNextMeeting.daysToStart >= DAYS_TO_WORKSHOP_MEETING_TO_ALERT
            ? ''
            : programClass?.isExpired || (!!programClass && programClass.daysToExpire <= 30)
            ? 'card-LMS__alert-title'
            : '';

    const alertWorkshopMeetingNameClass: string =
        workshopNextMeeting && workshopNextMeeting.daysToStart <= DAYS_TO_WORKSHOP_MEETING_TO_ALERT ? 'card-LMS__alert-title' : '';
    const enrollmentStatusClassName: string = courseEnrollment?.status === Constants.courseEnrollmentStatus.BLOCKED ? 'disabled' : '';
    const disabledBackgroundNameClass: string = disabled || isExpired ? 'card-LMS__expired-background' : '';
    const disabledClassName: string = disabled || isExpired ? 'card-LMS__expired' : '';

    const workload: number = useMemo(() => (course.workload || 0) + (workshop?.workload || 0), [course.workload, workshop?.workload]);

    const formattedNextMeetingOrClassEndDate: string | undefined = useMemo(() => {
        const workshopMeetingInProgress: WorkshopMeeting | undefined = workshop?.meetings.find(({ startDate, endDate }: WorkshopMeeting) => {
            const current = currentDate();
            return isAfter(current, startDate) && isBefore(current, endDate);
        });

        const meeting: WorkshopMeeting | undefined = workshopMeetingInProgress || workshopNextMeeting;

        if (meeting) {
            return courseService.formatWorkshopMeetingDate(meeting);
        } else if (programClass?.finishDate) {
            return getFormattedExpirationDate(programClass.finishDate, programClass.daysToExpire, programClass.isExpired);
        }

        return undefined;
    }, [workshopNextMeeting?.startDate, workshop?.meetings, programClass?.finishDate, strings._LANGUAGE]);

    useEffect(() => {
        if (courseEnrollment) {
            setPercentCompletedCourses(`${courseEnrollment.progressPercentage}%`);
        }
    }, [courseEnrollment]);

    return (
        <div className="main">
            <div
                className={` card-LMS ${disabledBackgroundNameClass} ${
                    mode === 'selection' && hasCurrentContent ? 'card-LMS__selected-list ' : ''
                } ${enrollmentStatusClassName} `}
            >
                <div className={`card-LMS__thumb ${disabledClassName}`}>
                    <div
                        className={`card-LMS__thumb-img ${enrollmentStatusClassName}`}
                        style={{ backgroundImage: `url('${getValidUrl(course.mainImage)}')` }}
                    />
                    {!isExpired && (
                        <div className="card-LMS__overlay-status">
                            {!hideStatus && courseService.getCourseStatusLabel(course, courseEnrollment as CourseEnrollment, strings, programClass)}
                        </div>
                    )}

                    <div className="card-LMS__overlay" />
                    {canFavorite && (
                        <FavoriteSection
                            buttonMode
                            customActiveClass="active"
                            customClass={`card-list__like`}
                            entityId={course.id as number}
                            entityName={course.name}
                            entityType={EntityType.COURSE}
                            moduleId={course.moduleId as number}
                        />
                    )}

                    {mode === 'selection' && !hasCurrentContent && (
                        <CourseSelection course={course} customActiveClass="active" customClass={`card-LMS__like`} />
                    )}
                </div>
                <div className="card-LMS__body">
                    <div className="card-LMS__container-flag">
                        <div className={`card-LMS__label ${disabledClassName}`}>{course.type === 'WORKSHOP' ? strings.WORKSHOP : strings.COURSE}</div>

                        {programClass && isExpireDateViewByStatus(courseEnrollment?.status) && (
                            <>
                                {course.type === 'WORKSHOP' && !programClass?.isExpired ? (
                                    <div
                                        className={`card-LMS__meeting-date ${
                                            !_.isEmpty(alertWorkshopMeetingNameClass) ? alertWorkshopMeetingNameClass : alertTitleNameClass
                                        }`}
                                    >
                                        {formattedNextMeetingOrClassEndDate}
                                    </div>
                                ) : (
                                    <div className={`card-LMS__expiration-date ${alertTitleNameClass}`}>
                                        {getFormattedExpirationDate(programClass?.finishDate, programClass.daysToExpire, programClass.isExpired)}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className={`card-LMS__title ${disabledClassName}`} title={course.name}>
                        {course.name}
                    </div>

                    <div className={`card-LMS__author ${disabledClassName}`} title={course.author}>
                        {course.author}
                    </div>

                    <div className={`card-LMS__footer ${disabledClassName}`}>
                        <div className="card-LMS__item-length">
                            {`${courseLessonsAmount === 0 && course.type === 'WORKSHOP' ? '-' : courseLessonsAmount} `}
                            {courseLessonsAmount === 1 ? strings.LESSON.toLowerCase() : strings.LESSONS.toLowerCase()}
                        </div>

                        <div className="card-LMS__duration">{courseService.formatWorkload(workload || 0, strings)}</div>

                        {mode === 'view' && (
                            <div className="card-LMS__rating">
                                <i className="material-icons">grade</i>
                                {(course.totalRating > 0 && course.rating?.toFixed(1)) || '-'}
                            </div>
                        )}
                    </div>

                    {course.type === 'REGULAR' && percentCompletedCourses ? (
                        <div className={`card-LMS__progressBarContainer ${disabledClassName}`}>
                            <div className="card-program__progressBarContainer__progressBar" style={{ width: percentCompletedCourses }} />
                        </div>
                    ) : (
                        <div className="card-LMS__workshopProgressBarContainer" />
                    )}
                </div>
            </div>
        </div>
    );
};

export default React.memo(LMSCourseCard);
